export const ENGAGEMENT_ZONE_DEFAULT_SHOT_COUNT = 1000;
export const ENGAGEMENT_ZONE_DEFAULT_TRANSONIC_SEVERITY_PERCENT = 50;
export const ENGAGEMENT_ZONE_TARGET_USPSA = "uspsa";
export const ENGAGEMENT_ZONE_TARGET_CUSTOM = "custom";

export const ENGAGEMENT_ZONE_FIELDS = Object.freeze({
  velocityExtremeSpread: "ezVelocityEs",
  rifleAccuracy: "ezRifleAccuracy",
  rangeUncertainty: "ezRangeUncertainty",
  windSpeedUncertainty: "ezWindSpeedUncertainty",
  windDirectionUncertainty: "ezWindDirectionUncertainty",
  targetType: "ezTargetType",
  targetWidth: "ezTargetWidth",
  targetHeight: "ezTargetHeight",
  transonicUncertainty: "ezTransonicUncertainty",
  transonicSeverity: "ezTransonicSeverity",
});

export const ENGAGEMENT_ZONE_FIELD_NAMES = Object.freeze(
  Object.values(ENGAGEMENT_ZONE_FIELDS),
);

export const USPSA_TARGET_WIDTH_MILLIMETERS = 450;
export const USPSA_TARGET_HEIGHT_MILLIMETERS = 750;
export const USPSA_TARGET_NON_SCORING_BORDER_MILLIMETERS = 5;
export const MILLIMETERS_PER_INCH = 25.4;

// Presentation copy of the supplied USPSA metric-target dimension drawing.
// The C calculator owns hit classification; this polygon draws the same
// physical outer edge in SVG.
export const USPSA_TARGET_POLYGON_MILLIMETERS = Object.freeze([
  [-75, 375],
  [75, 375],
  [75, 225],
  [175, 225],
  [225, 175],
  [225, -225],
  [150, -375],
  [-150, -375],
  [-225, -225],
  [-225, 175],
  [-175, 225],
  [-75, 225],
].map(([x, y]) => Object.freeze({ x, y })));

// Exact scoring geometry from the supplied dimension drawing. The border is
// the specified 5 mm perpendicular inset from the outer edge. The remaining
// vertices follow the drawing's cumulative horizontal and vertical dimensions.
const diagonalInset = USPSA_TARGET_NON_SCORING_BORDER_MILLIMETERS * Math.SQRT2;
const lowerInset = USPSA_TARGET_NON_SCORING_BORDER_MILLIMETERS * Math.sqrt(5);
const upperTransition = 400 - diagonalInset;
const lowerTransition = 675 - lowerInset;
const lowerScoringCornerX = (lowerTransition - 370) / 2;

export const USPSA_TARGET_SCORING_BORDER_MILLIMETERS = Object.freeze([
  [-70, 370],
  [70, 370],
  [70, 220],
  [upperTransition - 220, 220],
  [220, upperTransition - 220],
  [220, 2 * 220 - lowerTransition],
  [lowerScoringCornerX, -370],
  [-lowerScoringCornerX, -370],
  [-220, 2 * 220 - lowerTransition],
  [-220, upperTransition - 220],
  [220 - upperTransition, 220],
  [-70, 220],
].map(([x, y]) => Object.freeze({ x, y })));

export const USPSA_TARGET_ZONE_BOUNDARIES_MILLIMETERS = Object.freeze([
  Object.freeze({
    id: "body",
    points: Object.freeze([
      [-75, 225],
      [-150, 175],
      [-150, -105],
      [-100, -225],
      [-100, -370],
      [100, -370],
      [100, -225],
      [150, -105],
      [150, 175],
      [75, 225],
    ].map(([x, y]) => Object.freeze({ x, y }))),
  }),
  Object.freeze({
    id: "head-a",
    points: Object.freeze([
      [-50, 350], [50, 350], [50, 300], [-50, 300],
    ].map(([x, y]) => Object.freeze({ x, y }))),
  }),
  Object.freeze({
    id: "body-a",
    points: Object.freeze([
      [-75, 175], [75, 175], [75, -105], [-75, -105],
    ].map(([x, y]) => Object.freeze({ x, y }))),
  }),
]);

// Labels are centered within their dimensioned regions and are presentation
// aids only; the C engine continues to classify hits against the outer edge.
export const USPSA_TARGET_ZONE_LABELS_MILLIMETERS = Object.freeze([
  Object.freeze({ zone: "A", x: 0, y: 325 }),
  Object.freeze({ zone: "B", x: 0, y: 260 }),
  Object.freeze({ zone: "A", x: 0, y: 35 }),
  Object.freeze({ zone: "C", x: -112.5, y: 35 }),
  Object.freeze({ zone: "C", x: 112.5, y: 35 }),
  Object.freeze({ zone: "D", x: -185, y: 35 }),
  Object.freeze({ zone: "D", x: 185, y: 35 }),
]);

const STRICT_DECIMAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;
const SUMMARY_SCHEMA = Object.freeze([
  Object.freeze({ label: "Shot Count:", key: "shot count", unit: "" }),
  Object.freeze({ label: "Hits:", key: "hits", unit: "" }),
  Object.freeze({ label: "Misses:", key: "misses", unit: "" }),
  Object.freeze({ label: "Modeled Shots:", key: "modeled shots", unit: "" }),
  Object.freeze({
    label: "Unmodelable Shots:", key: "unmodelable shots", unit: "",
  }),
  Object.freeze({
    label: "Hit Probability:", key: "hit probability", unit: "%",
  }),
  Object.freeze({ label: "Target Type:", key: "target type", unit: "" }),
  Object.freeze({ label: "Target Width:", key: "target width", unit: "inches" }),
  Object.freeze({ label: "Target Height:", key: "target height", unit: "inches" }),
  Object.freeze({ label: "Seed:", key: "seed", unit: "" }),
  Object.freeze({ label: "Model:", key: "model", unit: "" }),
  Object.freeze({
    label: "Transonic Uncertainty:",
    key: "transonic uncertainty",
    unit: "",
  }),
  Object.freeze({
    label: "Transonic Severity:",
    key: "transonic severity",
    unit: "%",
  }),
]);
const IMPACT_HEADERS = Object.freeze(["Shot", "Horizontal", "Vertical", "Hit"]);
const IMPACT_UNITS = Object.freeze([
  "(number)", "(inches)", "(inches)", "(boolean)",
]);

function finiteNumber(value, label) {
  if (value === null || value === undefined) {
    throw new TypeError(`${label} is required.`);
  }
  const text = String(value).trim();
  if (!STRICT_DECIMAL.test(text)) throw new TypeError(`${label} must be numeric.`);
  const numeric = Number(text);
  if (!Number.isFinite(numeric)) throw new TypeError(`${label} must be numeric.`);
  return Object.is(numeric, -0) ? 0 : numeric;
}

function freezePolygon(points) {
  return Object.freeze(points.map(({ x, y }) => Object.freeze({ x, y })));
}

function millimeterPointToInches({ x, y }) {
  return Object.freeze({
    x: x / MILLIMETERS_PER_INCH,
    y: y / MILLIMETERS_PER_INCH,
  });
}

function uspsaScoringOverlayInches() {
  return Object.freeze({
    borderPolygon: Object.freeze(
      USPSA_TARGET_SCORING_BORDER_MILLIMETERS.map(millimeterPointToInches),
    ),
    zoneBoundaries: Object.freeze(USPSA_TARGET_ZONE_BOUNDARIES_MILLIMETERS.map((boundary) => (
      Object.freeze({
        id: boundary.id,
        points: Object.freeze(boundary.points.map(millimeterPointToInches)),
      })
    ))),
    zoneLabels: Object.freeze(USPSA_TARGET_ZONE_LABELS_MILLIMETERS.map(({ zone, x, y }) => (
      Object.freeze({
        zone,
        x: x / MILLIMETERS_PER_INCH,
        y: y / MILLIMETERS_PER_INCH,
      })
    ))),
  });
}

function engagementTarget(type, widthInches, heightInches) {
  if (!Number.isFinite(widthInches) || widthInches <= 0
    || !Number.isFinite(heightInches) || heightInches <= 0) {
    throw new RangeError("Engagement Zone target dimensions must be positive.");
  }
  if (type === ENGAGEMENT_ZONE_TARGET_USPSA) {
    const standardWidth = USPSA_TARGET_WIDTH_MILLIMETERS / MILLIMETERS_PER_INCH;
    const standardHeight = USPSA_TARGET_HEIGHT_MILLIMETERS / MILLIMETERS_PER_INCH;
    if (Math.abs(widthInches - standardWidth) > 1e-5
      || Math.abs(heightInches - standardHeight) > 1e-5) {
      throw new RangeError("USPSA target dimensions do not match Appendix B1.");
    }
    return Object.freeze({
      type,
      label: "USPSA target",
      widthInches,
      heightInches,
      polygonInches: freezePolygon(USPSA_TARGET_POLYGON_MILLIMETERS.map(({ x, y }) => ({
        x: x / MILLIMETERS_PER_INCH,
        y: y / MILLIMETERS_PER_INCH,
      }))),
      scoringOverlayInches: uspsaScoringOverlayInches(),
    });
  }
  if (type !== ENGAGEMENT_ZONE_TARGET_CUSTOM) {
    throw new RangeError("Engagement Zone target type must be USPSA or custom.");
  }
  const halfWidth = widthInches / 2;
  const halfHeight = heightInches / 2;
  return Object.freeze({
    type,
    label: "Custom target",
    widthInches,
    heightInches,
    polygonInches: freezePolygon([
      { x: -halfWidth, y: halfHeight },
      { x: halfWidth, y: halfHeight },
      { x: halfWidth, y: -halfHeight },
      { x: -halfWidth, y: -halfHeight },
    ]),
  });
}

function normalizedLabel(value) {
  return String(value ?? "").replace(/:$/, "").trim().toLowerCase();
}

function sectionNamed(result, title) {
  const expected = normalizedLabel(title);
  const matches = (result?.sections || []).filter((section) => (
    normalizedLabel(section.title) === expected
  ));
  if (matches.length > 1) {
    throw new TypeError(`The ${title} section is duplicated.`);
  }
  return matches[0] || null;
}

function scalarValues(section) {
  if (section?.tables?.length !== 1) {
    throw new TypeError("The Engagement Zone summary table schema is invalid.");
  }
  const rows = section.tables[0]?.rows;
  if (!Array.isArray(rows) || rows.length !== SUMMARY_SCHEMA.length) {
    throw new TypeError("The Engagement Zone summary table schema is invalid.");
  }
  const schemaByLabel = new Map(SUMMARY_SCHEMA.map((entry) => [entry.label, entry]));
  const values = new Map();
  for (const row of rows) {
    if (row?.header !== false || !Array.isArray(row.cells) || row.cells.length !== 3) {
      throw new TypeError("The Engagement Zone summary row schema is invalid.");
    }
    const [label, value, unit] = row.cells;
    const schema = schemaByLabel.get(label);
    if (!schema) {
      throw new TypeError("The Engagement Zone summary contains an unexpected scalar.");
    }
    if (values.has(schema.key)) {
      throw new TypeError(`The Engagement Zone summary duplicates ${label}`);
    }
    if (unit !== schema.unit) {
      throw new TypeError(`The Engagement Zone summary unit for ${label} is invalid.`);
    }
    values.set(schema.key, value);
  }
  if (values.size !== SUMMARY_SCHEMA.length) {
    throw new TypeError("The Engagement Zone summary is missing a required scalar.");
  }
  return values;
}

function rowMatches(row, expectedCells) {
  return row?.header === true
    && Array.isArray(row.cells)
    && row.cells.length === expectedCells.length
    && row.cells.every((cell, index) => cell === expectedCells[index]);
}

function impactCoordinate(value) {
  const text = String(value ?? "");
  if (text === "---") return null;
  try {
    return finiteNumber(text, "Impact coordinate");
  } catch {
    throw new TypeError("Engagement Zone impact coordinates are invalid.");
  }
}

function impactRows(section) {
  if (section?.tables?.length !== 1) {
    throw new TypeError("Engagement Zone impact table schema is invalid.");
  }
  const rows = section.tables[0]?.rows;
  if (!Array.isArray(rows) || !rowMatches(rows[0], IMPACT_HEADERS)) {
    throw new TypeError("Engagement Zone impact headers are invalid.");
  }
  if (!rowMatches(rows[1], IMPACT_UNITS)) {
    throw new TypeError("Engagement Zone impact units are invalid.");
  }
  return rows.slice(2).map((row, rowIndex) => {
    if (row?.header !== false || !Array.isArray(row.cells) || row.cells.length !== 4) {
      throw new TypeError("Engagement Zone impact row schema is invalid.");
    }
    const [shotValue, horizontalValue, verticalValue, hitValue] = row.cells;
    const horizontal = impactCoordinate(horizontalValue);
    const vertical = impactCoordinate(verticalValue);
    const horizontalModelable = horizontal !== null;
    const verticalModelable = vertical !== null;
    if (horizontalModelable !== verticalModelable) {
      throw new TypeError("Engagement Zone impact coordinates are inconsistent.");
    }
    const modelable = horizontalModelable && verticalModelable;
    const shot = finiteNumber(shotValue, "Shot number");
    const hitText = String(hitValue ?? "");
    if (!Number.isInteger(shot) || shot !== rowIndex + 1
      || !["yes", "no"].includes(hitText) || (!modelable && hitText === "yes")) {
      throw new TypeError("Engagement Zone impact row is invalid.");
    }
    return Object.freeze({
      index: shot - 1,
      xInches: horizontal,
      yInches: vertical,
      hit: hitText === "yes",
      modelable,
    });
  });
}

/** Convert the dedicated C CGI's structured tables to render-only data. */
export function extractEngagementZoneResult(result) {
  if (result?.error) return Object.freeze({ error: String(result.error) });
  const summarySection = sectionNamed(result, "Engagement Zone Summary");
  const impactsSection = sectionNamed(result, "Engagement Zone Impacts");
  if (!summarySection || !impactsSection) {
    throw new TypeError("The Engagement Zone CGI response is incomplete.");
  }
  const summary = scalarValues(summarySection);
  const shotCount = finiteNumber(summary.get("shot count"), "Shot count");
  const hitCount = finiteNumber(summary.get("hits"), "Hit count");
  const missCount = finiteNumber(summary.get("misses"), "Miss count");
  const reportedModeledShotCount = finiteNumber(
    summary.get("modeled shots"), "Modeled shot count",
  );
  const reportedUnmodelableShotCount = finiteNumber(
    summary.get("unmodelable shots"), "Unmodelable shot count",
  );
  const hitPercentage = finiteNumber(
    summary.get("hit probability"), "Hit probability",
  );
  const targetType = normalizedLabel(summary.get("target type"));
  const widthInches = finiteNumber(summary.get("target width"), "Target width");
  const heightInches = finiteNumber(summary.get("target height"), "Target height");
  const modelVersion = String(summary.get("model") ?? "").trim();
  const seed = String(summary.get("seed") ?? "").trim();
  const transonicUncertaintyState = summary.get("transonic uncertainty");
  const transonicSeverityPercent = finiteNumber(
    summary.get("transonic severity"), "Transonic severity",
  );
  const impacts = impactRows(impactsSection);
  const modeledShotCount = impacts.filter(({ modelable }) => modelable).length;
  const unmodelableShotCount = shotCount - modeledShotCount;
  if (![shotCount, hitCount, missCount, reportedModeledShotCount,
    reportedUnmodelableShotCount].every(Number.isInteger)
    || shotCount !== ENGAGEMENT_ZONE_DEFAULT_SHOT_COUNT
    || hitCount < 0 || missCount < 0
    || shotCount !== impacts.length || hitCount + missCount !== shotCount
    || reportedModeledShotCount !== modeledShotCount
    || reportedUnmodelableShotCount !== unmodelableShotCount
    || reportedModeledShotCount + reportedUnmodelableShotCount !== shotCount
    || hitCount !== impacts.filter(({ hit }) => hit).length
    || hitPercentage < 0 || hitPercentage > 100
    || Math.abs(hitPercentage - 100 * hitCount / shotCount) > 0.051
    || !["enabled", "disabled"].includes(transonicUncertaintyState)
    || transonicSeverityPercent < 0 || transonicSeverityPercent > 100
    || (transonicUncertaintyState === "disabled" && transonicSeverityPercent !== 0)
    || modelVersion === "" || !/^\d+$/.test(seed)
    || !Number.isSafeInteger(Number(seed))
    || Number(seed) < 1 || Number(seed) > 0xffffffff) {
    throw new TypeError("The Engagement Zone CGI summary is inconsistent.");
  }
  return Object.freeze({
    modelVersion,
    seed,
    transonicUncertaintyEnabled: transonicUncertaintyState === "enabled",
    transonicSeverityPercent,
    shotCount,
    modeledShotCount,
    unmodelableShotCount,
    hitCount,
    missCount,
    hitProbability: shotCount ? hitCount / shotCount : 0,
    hitPercentage,
    target: engagementTarget(targetType, widthInches, heightInches),
    impacts: Object.freeze(impacts),
  });
}
